/**
 * Client-side mirror of the server's role gating
 * (`packages/server/src/apps/vault/services/permissions.ts`).
 *
 * These hooks drive UI affordances — hiding Export/Orphanage/Restore buttons,
 * putting the document editor in read-only mode for viewers, etc. The server
 * is the authoritative enforcer; these hooks just prevent the user from being
 * shown controls that would 403. Keep this logic in sync with the server's
 * canEdit / canAdminister definitions.
 */
import { useVault } from '../../../core/lib/vault';
import type { VaultRole } from '@dragonhq/protocol';

export function useVaultRole(): VaultRole | null {
  return useVault((s) => s.vaultRole);
}

/** owner | admin | editor can mutate live data. Viewer is read-only. */
export function useCanEdit(): boolean {
  const role = useVaultRole();
  return role === 'owner' || role === 'admin' || role === 'editor';
}

/**
 * owner | admin can perform destructive or bulk admin ops: export the vault,
 * restore from the orphanage, restore historical document versions, invite
 * or remove vault members.
 */
export function useCanAdminister(): boolean {
  const role = useVaultRole();
  return role === 'owner' || role === 'admin';
}
